import type { Person, Task } from '@cerebro-diem/core';
import { fileCaptureInto, insertCapture } from './captures';
import { LIST_LIMIT } from './queries';
import { signedInUserId, supabase } from './supabase';

export type PersonUpdate = Partial<Pick<Person, 'name' | 'context'>>;

/** A person as the People list shows them: with how many open follow-ups (linked open tasks) they have. */
export type ListedPerson = Person & { openFollowUps: number };

type TaskLink = { tasks: { status: string } | { status: string }[] | null };
/** The open tasks among a person's links; PostgREST may hand each task over as an object or a one-item list. */
const openLinked = (links: TaskLink[] | null | undefined): number =>
  (links ?? []).flatMap((link) => (Array.isArray(link.tasks) ? link.tasks : link.tasks ? [link.tasks] : [])).filter((task) => task.status !== 'done').length;

/** A person's follow-ups (CER-140): the open tasks linked to them, soonest due first, then the oldest. */
export async function personFollowUps(personId: string): Promise<Task[]> {
  const { data, error } = await supabase.from('task_people').select('task:tasks(*)').eq('person_id', personId);
  if (error) throw error;
  return ((data ?? []) as unknown as { task: Task | Task[] | null }[])
    .flatMap((row) => (Array.isArray(row.task) ? row.task : row.task ? [row.task] : []))
    .filter((task) => task.status !== 'done')
    .sort((a, b) =>
      a.due_date && b.due_date ? a.due_date.localeCompare(b.due_date) : a.due_date ? -1 : b.due_date ? 1 : a.created_at.localeCompare(b.created_at),
    );
}

/** Add a follow-up with a person: a capture of the words, filed as a next action linked to them, the one way every task is made. */
export async function addPersonFollowUp(
  personId: string,
  words: string,
  dueDate: string | null,
  container: { kind: 'project' | 'area'; id: string },
): Promise<void> {
  const title = words.trim();
  if (!title) throw new Error('a follow-up needs words');
  const ownerId = await signedInUserId();
  const createdAt = new Date().toISOString();
  const captureId = await insertCapture(title, 'text', crypto.randomUUID(), ownerId, createdAt);
  await fileCaptureInto(captureId, {
    kind: 'task', title, due_date: dueDate, reminder: null, notes: null, gtd_status: 'next', context: null,
    container, personIds: [personId], newPersonNames: [],
  });
}

/** A name with its surrounding space removed; a blank one is refused before anything is written. */
function requiredName(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) throw new Error('a person needs a name');
  return trimmed;
}

/** People whose name contains `search` (all of them when it is empty), most recently in touch first, up to LIST_LIMIT. */
export async function listPeople(search: string): Promise<ListedPerson[]> {
  let query = supabase.from('people').select('*, task_people(tasks(status))').order('last_touched', { ascending: false }).limit(LIST_LIMIT);
  if (search) query = query.ilike('name', `%${search}%`);
  const { data, error } = await query;
  if (error) throw error;
  return ((data ?? []) as unknown as (Person & { task_people?: TaskLink[] | null })[]).map(({ task_people: links, ...person }) => ({
    ...person,
    openFollowUps: openLinked(links),
  }));
}

/** One person, or null when they do not exist (or are not yours). */
export async function getPerson(id: string): Promise<Person | null> {
  const { data, error } = await supabase.from('people').select('*').eq('id', id).maybeSingle();
  if (error) throw error;
  return (data ?? null) as Person | null;
}

/** Add a person by hand, as the signed-in user, and return them. The context is optional. */
export async function createPerson(name: string, context: string): Promise<Person> {
  const trimmed = requiredName(name);
  const userId = await signedInUserId();
  const { data, error } = await supabase
    .from('people')
    .insert({ user_id: userId, name: trimmed, context: context.trim() || null })
    .select('*')
    .single();
  if (error) throw error;
  if (!data) throw new Error('the server did not confirm the new person');
  return data as Person;
}

/**
 * Save an edit to one person and return them as saved. An edit counts as
 * being in touch, as it does on Android, so last_touched moves to now.
 * Throws when nothing was updated, so a silent no-op is never shown as saved.
 */
export async function updatePerson(id: string, update: PersonUpdate): Promise<Person> {
  const changes = update.name === undefined ? update : { ...update, name: requiredName(update.name) };
  const { data, error } = await supabase
    .from('people')
    .update({ ...changes, last_touched: new Date().toISOString() })
    .eq('id', id)
    .select('*')
    .maybeSingle();
  if (error) throw error;
  if (!data) throw new Error('person not found; they may have been deleted');
  return data as Person;
}

/**
 * Delete one person. Their links to tasks and notes go with them (the link
 * tables cascade); the tasks and notes themselves stay. Throws when nothing
 * was deleted.
 */
export async function deletePerson(id: string): Promise<void> {
  const { data, error } = await supabase.from('people').delete().eq('id', id).select('id');
  if (error) throw error;
  if (!data || data.length === 0) throw new Error('person not found; they may have been deleted');
}
